if (!customElements.get('student-bundle-builder')) {
  class StudentBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.groups = Array.from(this.querySelectorAll('[data-choice-group]'));
      this.summary = this.querySelector('[data-summary-selections]');
      this.status = this.querySelector('[data-cart-status]');
      this.buttons = Array.from(this.querySelectorAll('[data-cart-cta], [data-cart-cta-mobile]'));
      this.adding = false;

      this.groups.forEach((group) => {
        if (group.dataset.selectionMode === 'quantity') {
          this.updateQuantityGroup(group);
          return;
        }
        if (!group.querySelector('[data-choice]:checked:not(:disabled)')) {
          const first = group.querySelector('[data-choice]:not(:disabled)');
          if (first) first.checked = true;
        }
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-choice]')) this.update();
      });
      this.addEventListener('click', (event) => {
        const control = event.target.closest('[data-quantity-plus], [data-quantity-minus]');
        if (!control) return;
        const item = control.closest('[data-quantity-choice]');
        const group = control.closest('[data-choice-group]');
        if (!item || !group) return;
        const limit = Number(group.dataset.choiceLimit || 2);
        const total = this.quantityTotal(group);
        const current = Number(item.dataset.quantity || 0);
        if (control.matches('[data-quantity-plus]') && item.dataset.choiceAvailable === 'true' && total < limit) {
          item.dataset.quantity = String(current + 1);
        }
        if (control.matches('[data-quantity-minus]') && current > 0) {
          item.dataset.quantity = String(current - 1);
        }
        this.updateQuantityGroup(group);
        this.update();
      });
      this.buttons.forEach((button) => button.addEventListener('click', () => this.handleCartClick()));
      this.update();
    }

    quantityTotal(group) {
      return Array.from(group.querySelectorAll('[data-quantity-choice]'))
        .reduce((sum, item) => sum + Number(item.dataset.quantity || 0), 0);
    }

    updateQuantityGroup(group) {
      const limit = Number(group.dataset.choiceLimit || 2);
      const total = this.quantityTotal(group);
      const count = group.querySelector('[data-group-count]');
      if (count) count.textContent = `${total}/${limit} selected`;
      group.querySelectorAll('[data-quantity-choice]').forEach((item) => {
        const quantity = Number(item.dataset.quantity || 0);
        const value = item.querySelector('[data-quantity-value]');
        const minus = item.querySelector('[data-quantity-minus]');
        const plus = item.querySelector('[data-quantity-plus]');
        item.dataset.quantitySelected = quantity > 0 ? 'true' : 'false';
        if (value) value.textContent = String(quantity);
        if (minus) minus.disabled = quantity === 0 || this.adding;
        if (plus) plus.disabled = this.adding || item.dataset.choiceAvailable !== 'true' || total >= limit;
      });
    }

    fixedItemsValid() {
      return Array.from(this.querySelectorAll('[data-fixed-item]'))
        .every((item) => item.dataset.fixedAvailable === 'true' && item.dataset.fixedPriceValid === 'true' && item.dataset.fixedVariant);
    }

    addItem(itemMap, variantId, quantity) {
      const id = Number(variantId);
      const qty = Number(quantity);
      if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(qty) || qty <= 0) return false;
      itemMap.set(id, (itemMap.get(id) || 0) + qty);
      return true;
    }

    cartItems() {
      const itemMap = new Map();
      let valid = true;

      this.groups.forEach((group) => {
        if (group.dataset.selectionMode === 'quantity') {
          const limit = Number(group.dataset.choiceLimit || 2);
          if (this.quantityTotal(group) !== limit) valid = false;
          group.querySelectorAll('[data-quantity-choice]').forEach((item) => {
            const quantity = Number(item.dataset.quantity || 0);
            if (!quantity) return;
            if (item.dataset.choiceAvailable !== 'true' || !this.addItem(itemMap, item.dataset.choiceVariant, quantity)) valid = false;
          });
          return;
        }

        const input = group.querySelector('[data-choice]:checked:not(:disabled)');
        if (!input || input.dataset.choiceAvailable !== 'true' || input.dataset.choicePriceValid !== 'true') {
          valid = false;
          return;
        }
        if (!this.addItem(itemMap, input.dataset.choiceVariant, input.dataset.choicePrimaryQuantity || 1)) valid = false;
        if (input.dataset.choiceSecondaryVariant && !this.addItem(itemMap, input.dataset.choiceSecondaryVariant, input.dataset.choiceSecondaryQuantity || 1)) valid = false;
      });

      this.querySelectorAll('[data-fixed-item]').forEach((item) => {
        if (item.dataset.fixedAvailable !== 'true' || item.dataset.fixedPriceValid !== 'true' || !this.addItem(itemMap, item.dataset.fixedVariant, item.dataset.fixedQuantity || 1)) valid = false;
      });

      return {
        valid,
        items: Array.from(itemMap, ([id, quantity]) => ({ id, quantity }))
      };
    }

    update() {
      const selections = this.groups.map((group) => {
        const label = group.querySelector('legend strong')?.textContent?.trim() || 'Choice';
        if (group.dataset.selectionMode === 'quantity') {
          const limit = Number(group.dataset.choiceLimit || 2);
          const total = this.quantityTotal(group);
          const selected = Array.from(group.querySelectorAll('[data-quantity-choice]'))
            .map((item) => ({ label: item.dataset.choiceLabel, quantity: Number(item.dataset.quantity || 0) }))
            .filter((item) => item.quantity > 0);
          return {
            label,
            complete: total === limit,
            value: selected.length
              ? selected.map((item) => `${item.label} × ${item.quantity}`).join(', ')
              : `${total}/${limit} selected`
          };
        }
        const input = group.querySelector('[data-choice]:checked:not(:disabled)');
        return { label, complete: Boolean(input), value: input?.dataset.choiceLabel || 'Choose an option' };
      });
      const choicesComplete = selections.every((selection) => selection.complete);
      const fixedValid = this.fixedItemsValid();
      const payload = this.cartItems();
      const complete = choicesComplete && fixedValid && payload.valid && payload.items.length > 0;

      if (this.summary) {
        this.summary.innerHTML = '';
        selections.forEach(({ label, value }) => {
          const row = document.createElement('div');
          row.className = 'student-bundle__summary-row';
          const title = document.createElement('span');
          title.textContent = label;
          const choice = document.createElement('strong');
          choice.textContent = value;
          row.append(title, choice);
          this.summary.append(row);
        });
        const fixedCount = this.querySelectorAll('[data-fixed-item]').length;
        if (fixedCount) {
          const row = document.createElement('div');
          row.className = 'student-bundle__summary-row';
          const title = document.createElement('span');
          title.textContent = 'Fixed essentials';
          const value = document.createElement('strong');
          value.textContent = `${fixedCount} included`;
          row.append(title, value);
          this.summary.append(row);
        }
      }

      this.readyToAdd = complete;
      this.buttons.forEach((button) => { button.disabled = !complete || this.adding; });
      this.groups.filter((group) => group.dataset.selectionMode === 'quantity').forEach((group) => this.updateQuantityGroup(group));
      if (this.status && !this.adding) {
        if (!fixedValid) this.status.textContent = 'A fixed pack item is unavailable or no longer matches the validated price. This pack cannot be added.';
        else this.status.textContent = choicesComplete ? '' : 'Complete each choice before adding the items.';
      }
    }

    async handleCartClick() {
      if (this.adding) return;
      const payload = this.cartItems();
      if (!this.readyToAdd || !payload.valid || !payload.items.length) {
        if (this.status) this.status.textContent = 'Please complete every valid choice before adding the items.';
        return;
      }

      this.adding = true;
      this.buttons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
      if (this.status) this.status.textContent = 'Adding your selected items to the cart…';

      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload.items })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'The selected items could not be added.');
        window.location.assign(`${root}cart`);
      } catch (error) {
        this.adding = false;
        this.buttons.forEach((button) => button.removeAttribute('aria-busy'));
        this.update();
        if (this.status) {
          this.status.textContent = `${error.message} Please review your choices and try again.`;
          this.status.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }
  customElements.define('student-bundle-builder', StudentBundleBuilder);
}
